import { Helmet } from 'react-helmet-async';
import usePrerenderReady from '../hooks/usePrerenderReady';
import { SITE_URL } from '../seoConfig';
import { toJsonLd } from '../schema';

/**
 * Target query/intent: "RosterRx FAQ" / common questions about the rankings aggregator and
 * draft assistant. Q&A pairs are written direct-answer-first (R4.2) so both a skimming user and
 * an AI extracting a quotable answer get the point in the first sentence.
 */
const FAQS = [
  {
    question: 'What is RosterRx?',
    answer:
      'RosterRx is a fantasy football rankings aggregator and live draft assistant. It combines expert rankings from ESPN, Yahoo, and FantasyPros into one consensus view, and offers an AI-powered draft assistant for pick suggestions, round predictions, and season-long lineup and trade advice.',
  },
  {
    question: 'Is RosterRx free?',
    answer:
      'Yes. Rankings aggregation from up to 3 sources and CSV import/export are free with no account required. A $5/month Pro tier adds live draft mode with AI pick suggestions, mock draft practice, unlimited ranking sources, multi-league support, and in-season AI tools.',
  },
  {
    question: 'Where do the rankings come from?',
    answer:
      'RosterRx aggregates publicly available expert rankings from ESPN, Yahoo, and FantasyPros into a single consensus ranking. Each source is shown individually too, so you can compare where experts agree or disagree on a player.',
  },
  {
    question: 'Is RosterRx affiliated with ESPN, Yahoo, or FantasyPros?',
    answer:
      'No. RosterRx is an independent product. It aggregates publicly available rankings and league data from those platforms but is not affiliated with, endorsed by, or sponsored by any of them.',
  },
  {
    question: 'What does the AI draft assistant do?',
    answer:
      "During a live snake draft, RosterRx's AI (Pro) suggests the best available player for your roster needs, predicts what other teams are likely to draft in the upcoming round, and factors in an opponent's past draft tendencies if you've tracked them as a manager profile.",
  },
  {
    question: 'Can I import my existing Yahoo or ESPN league?',
    answer:
      "Yes. RosterRx can import a Yahoo or ESPN league, including each team's full draft history where available, so you don't have to rebuild your league or draft history from scratch.",
  },
];

const FAQ_PAGE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
};

function FaqView() {
  usePrerenderReady();

  return (
    <div>
      <Helmet>
        <title>FAQ — RosterRx Fantasy Football Rankings & Draft Assistant</title>
        <script type='application/ld+json'>{toJsonLd(FAQ_PAGE_SCHEMA)}</script>
        <meta
          name='description'
          content="Common questions about RosterRx: what it is, whether it's free, where rankings come from, and what the AI draft assistant does."
        />
        <link rel='canonical' href={`${SITE_URL}/faq`} />
        <meta property='og:title' content='FAQ — RosterRx Fantasy Football Rankings & Draft Assistant' />
        <meta
          property='og:description'
          content="Common questions about RosterRx: what it is, whether it's free, where rankings come from, and what the AI draft assistant does."
        />
        <meta property='og:url' content={`${SITE_URL}/faq`} />
        <meta property='og:type' content='website' />
        <meta name='twitter:card' content='summary' />
        <meta name='twitter:title' content='FAQ — RosterRx Fantasy Football Rankings & Draft Assistant' />
        <meta
          name='twitter:description'
          content="Common questions about RosterRx: what it is, whether it's free, where rankings come from, and what the AI draft assistant does."
        />
      </Helmet>
      <div className='card'>
        <h2 style={{ marginTop: 0 }}>Frequently Asked Questions</h2>
        {FAQS.map((faq) => (
          <div key={faq.question} style={{ marginBottom: 20 }}>
            <p style={{ fontWeight: 700, marginBottom: 4 }}>{faq.question}</p>
            <p style={{ margin: 0, color: 'var(--text-muted)' }}>{faq.answer}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default FaqView;
